// ============================================
// Lift Gamma Gain Post-Process Effect
// ============================================

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { LIFT_GAMMA_GAIN_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Lift, Gamma, Gain post-processing effect
 * Traditional color grading controls for shadows, midtones, and highlights
 */
export class LiftGammaGainEffect extends PostProcessEffect {
  readonly type = 'liftGammaGain';
  readonly displayName = 'Lift Gamma Gain';
  
  // Lift (shadows) - typically -1 to 1
  liftR = 0.0;
  liftG = 0.0;
  liftB = 0.0;
  
  // Gamma (midtones) - typically 0.1 to 3
  gammaR = 1.0;
  gammaG = 1.0;
  gammaB = 1.0;
  
  // Gain (highlights) - typically 0 to 3
  gainR = 1.0;
  gainG = 1.0;
  gainB = 1.0;
  
  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = LIFT_GAMMA_GAIN_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      { name: 'liftR', type: 'float', min: -1, max: 1, default: 0.0, description: 'Lift Red (shadows)' },
      { name: 'liftG', type: 'float', min: -1, max: 1, default: 0.0, description: 'Lift Green (shadows)' },
      { name: 'liftB', type: 'float', min: -1, max: 1, default: 0.0, description: 'Lift Blue (shadows)' },
      { name: 'gammaR', type: 'float', min: 0.1, max: 3, default: 1.0, description: 'Gamma Red (midtones)' },
      { name: 'gammaG', type: 'float', min: 0.1, max: 3, default: 1.0, description: 'Gamma Green (midtones)' },
      { name: 'gammaB', type: 'float', min: 0.1, max: 3, default: 1.0, description: 'Gamma Blue (midtones)' },
      { name: 'gainR', type: 'float', min: 0, max: 3, default: 1.0, description: 'Gain Red (highlights)' },
      { name: 'gainG', type: 'float', min: 0, max: 3, default: 1.0, description: 'Gain Green (highlights)' },
      { name: 'gainB', type: 'float', min: 0, max: 3, default: 1.0, description: 'Gain Blue (highlights)' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'liftR': return this.liftR;
      case 'liftG': return this.liftG;
      case 'liftB': return this.liftB;
      case 'gammaR': return this.gammaR;
      case 'gammaG': return this.gammaG;
      case 'gammaB': return this.gammaB;
      case 'gainR': return this.gainR;
      case 'gainG': return this.gainG;
      case 'gainB': return this.gainB;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'liftR': this.liftR = value; break;
      case 'liftG': this.liftG = value; break;
      case 'liftB': this.liftB = value; break;
      case 'gammaR': this.gammaR = value; break;
      case 'gammaG': this.gammaG = value; break;
      case 'gammaB': this.gammaB = value; break;
      case 'gainR': this.gainR = value; break;
      case 'gainG': this.gainG = value; break;
      case 'gainB': this.gainB = value; break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const liftGammaGainWgsl = getCompiledShader('postprocess/lift-gamma-gain');
    this.shader = ctx.createShader(liftGammaGainWgsl, liftGammaGainWgsl);
    
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'lift-gamma-gain-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'lift-gamma-gain-ldr',
    });
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, isHdrTarget } = context;
    
    // NOTE: PostProcessStack already set render target and viewport
    
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    this.uniformWriter.setAll({
      lift: [lerp(0, this.liftR, intensity), lerp(0, this.liftG, intensity), lerp(0, this.liftB, intensity)],
      _pad0: 0,
      gamma: [lerp(1, this.gammaR, intensity), lerp(1, this.gammaG, intensity), lerp(1, this.gammaB, intensity)],
      _pad1: 0,
      gain: [lerp(1, this.gainR, intensity), lerp(1, this.gainG, intensity), lerp(1, this.gainB, intensity)],
      _pad2: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
